import { useNavigate } from "react-router-dom";
import { useCallback, useContext, useState } from "react";
import {
  Settings,
  LogOut,
  Banknote,
  Megaphone,
  HelpCircle,
  ArrowLeftRight,
  Wallet,
  Users,
} from "lucide-react";
import { useStytchB2BClient } from "@stytch/react/b2b";
import MerchantContext from "../contexts/MerchantContext";
import UserContext from "../contexts/UserContext";
import OrganizationSwitcher from "./OrganizationSwitcher";
import AccountAvatar from "./AccountAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "../trpc";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  isDataScopedEmployee,
  isRestrictedEmployee,
} from "@/utils/permissions";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const MobileMoreMenu = ({ isOpen, onClose }: Props) => {
  const stytch = useStytchB2BClient();
  const navigate = useNavigate();
  const { helpCenterUrl, broadcastEnabled, collectionsEnabled, displayName } =
    useContext(MerchantContext);
  const { roles, email, isLoading: isUserLoading } = useContext(UserContext);

  const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);
  const { data: userInfo } = trpc.auth.getUserInfo.useQuery();
  const { data: orgsData } = trpc.auth.getMemberOrganizations.useQuery();

  const isDataScoped = isDataScopedEmployee(roles);
  const isRestricted = isRestrictedEmployee(roles);
  const hasMultipleOrgs = orgsData?.organizations && orgsData.organizations.length > 1;

  const handleLogout = useCallback(async () => {
    await stytch.session.revoke();
    navigate("/signup");
  }, [stytch, navigate]);

  const menuItems = [
    // Tablero is in RESTRICTED_EMPLOYEE_ALLOWED_PATHS, so it stays visible to
    // everyone; the page links below it are outside that list and hide for
    // restricted employees.
    { label: "Tablero", icon: Users, action: () => { navigate("/tablero"); onClose(); } },
    ...(broadcastEnabled && !isRestricted
      ? [
          { label: "Envío masivo", icon: Megaphone, action: () => { navigate("/broadcast"); onClose(); } },
        ]
      : []),
    ...(collectionsEnabled && !isRestricted
      ? [{ label: "Recaudo", icon: Wallet, action: () => { navigate("/recaudo"); onClose(); } }]
      : []),
    ...(!isDataScoped
      ? [{ label: "Consumo", icon: Banknote, action: () => { navigate("/consumption"); onClose(); } }]
      : []),
    ...(!isDataScoped
      ? [{ label: "Configuración", icon: Settings, action: () => { navigate("/configuration"); onClose(); } }]
      : []),
    ...(helpCenterUrl
      ? [{ label: "Centro de ayuda", icon: HelpCircle, action: () => { window.open(helpCenterUrl, "_blank"); onClose(); } }]
      : []),
    ...(hasMultipleOrgs
      ? [{ label: "Cambiar organización", icon: ArrowLeftRight, action: () => { setIsOrgSwitcherOpen(true); onClose(); } }]
      : []),
    { label: "Cerrar sesión", icon: LogOut, action: handleLogout },
  ];

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="bottom" className="rounded-t-xl max-h-[70vh]">
          <SheetHeader className="border-b pb-4">
            <SheetTitle>Más opciones</SheetTitle>
          </SheetHeader>
          {/* Mobile has no hover, so the session identity is always on screen —
              directly above the account actions it belongs to. */}
          {(isUserLoading || email) && (
            <div className="flex items-center gap-3 border-b py-4">
              {!email ? (
                <>
                  <Skeleton className="size-8 rounded-full" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-44 max-w-full" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </>
              ) : (
                <>
                  <AccountAvatar email={email} />
                  <div className="min-w-0">
                    <p className="break-all text-sm font-medium text-palomma-secondary">
                      {email}
                    </p>
                    {displayName && (
                      <p className="break-words text-xs text-muted-foreground">
                        {displayName}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="flex flex-col py-4">
            {menuItems.map((item, index) => (
              <button
                key={index}
                className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md px-4 py-3 hover:bg-gray-50"
                onClick={item.action}
              >
                <item.icon className="size-5 text-gray-600" />
                <span className="text-base text-gray-700">{item.label}</span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
      <OrganizationSwitcher
        isOpen={isOrgSwitcherOpen}
        onClose={() => setIsOrgSwitcherOpen(false)}
        currentOrganizationId={userInfo?.organization?.id}
      />
    </>
  );
};

export default MobileMoreMenu;
